import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Table, Badge, Input, Row, Col, Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { getTicketsByPmUid } from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";
import homeBg from "../../../assets/images/home.png";

const SEVERITY_COLORS = {
  Low: "info",
  Medium: "warning",
  High: "orange",
  Urgent: "danger",
};

const STATUS_COLORS = {
  Open: "primary",
  "In Progress": "warning",
  Resolved: "success",
  Closed: "secondary",
  Done: "success",
};

const TicketList = () => {
  document.title = "Tickets | Support Portal";

  const user = useAuthUser();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("");

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setTickets(await getTicketsByPmUid(user.uid));
      } catch (e) {
        console.error("Failed to load tickets:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const filtered = tickets.filter((t) => {
    if (filterStatus && t.status !== filterStatus) return false;
    if (filterSeverity && t.severity !== filterSeverity) return false;
    if (filterPeriod && t.createdAt) {
      const now = new Date();
      const start = new Date();
      if (filterPeriod === "today") {
        start.setHours(0, 0, 0, 0);
      } else if (filterPeriod === "week") {
        const day = start.getDay();
        start.setDate(start.getDate() - day);
        start.setHours(0, 0, 0, 0);
      } else if (filterPeriod === "month") {
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
      } else if (filterPeriod === "year") {
        start.setMonth(0, 1);
        start.setHours(0, 0, 0, 0);
      }
      if (filterPeriod !== "all" && t.createdAt < start) return false;
    } else if (filterPeriod && !t.createdAt) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        {/* ── Hero Banner ──────────────────────────────────────────────────── */}
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 180 }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${homeBg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.18 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-1 text-white">Tickets</h3>
                <p className="mb-0 opacity-75">All support tickets across your properties.</p>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs="auto">
                <div className="d-flex align-items-center gap-2">
                  <Badge color="light" className="text-dark fs-6 px-3 py-2">
                    <i className="bx bx-task me-1" />
                    {tickets.length} Total
                  </Badge>
                  <Badge color="light" className="text-dark fs-6 px-3 py-2">
                    <i className="bx bx-error-circle me-1" />
                    {tickets.filter(t => t.status === "Open").length} Open
                  </Badge>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-3">
          <CardBody>
            <Row>
              <Col md={4}>
                <Input
                  type="select"
                  value={filterPeriod}
                  onChange={(e) => setFilterPeriod(e.target.value)}
                >
                  <option value="">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                </Input>
              </Col>
              <Col md={4}>
                <Input
                  type="select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </Input>
              </Col>
              <Col md={4}>
                <Input
                  type="select"
                  value={filterSeverity}
                  onChange={(e) => setFilterSeverity(e.target.value)}
                >
                  <option value="">All Severities</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </Input>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Ticket table */}
        <Card>
          <CardBody>
            {filtered.length === 0 ? (
              <div className="text-center py-4">
                <h5 className="text-muted">No tickets found</h5>
                <p className="text-muted">
                  {tickets.length === 0
                    ? "No tickets have been submitted yet."
                    : "Try adjusting your filters."}
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table className="table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Tenant</th>
                      <th>Property</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <Link to={`/tickets/${t.id}`}>
                            {t.title}
                          </Link>
                        </td>
                        <td className="text-muted">{t.tenantName || "—"}</td>
                        <td>
                          {t.propertyId ? (
                            <Link to={`/properties/${t.propertyId}`}>
                              {t.propertyAddress || "View"}
                            </Link>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <Badge color={SEVERITY_COLORS[t.severity] || "secondary"}>
                            {t.severity}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={STATUS_COLORS[t.status] || "secondary"}>
                            {t.status}
                          </Badge>
                        </td>
                        <td>
                          {t.createdAt
                            ? t.createdAt.toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default TicketList;
